import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBluetoothStore } from '../../../store/bluetooth.store';
import { LABEL_SIZES, useLabelTemplateStore } from '../../../store/label-template.store';
import { useTranslation } from 'react-i18next';

interface CargoCreateHeaderProps {
    batchNo?: string;
    totalCount?: number;
    totalWeight?: number;
}

const CargoCreateHeader: React.FC<CargoCreateHeaderProps> = ({ batchNo, totalCount = 0, totalWeight = 0 }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { scaleDevice, printerDevice } = useBluetoothStore();
    const { templates, featureTemplates, setFeatureTemplate, initDefaults } = useLabelTemplateStore();
    const hasAnyDevice = !!(scaleDevice || printerDevice);
    const selectedTemplateId = featureTemplates.cargo_create || templates[0]?.id || '';

    useEffect(() => {
        initDefaults();
    }, [initDefaults]);

    return (
        <header className="bg-white dark:bg-surface-dark/95 backdrop-blur shadow-md z-50 sticky top-0 shrink-0">
            <div className="px-4 py-2 flex justify-between items-center text-[10px] text-gray-400 border-b border-white/5 flex-wrap gap-y-1">
                <div className="flex items-center gap-4">
                    <span className="font-mono tracking-wider">{t('common.device_no', '设备号')}: NT20-001</span>
                    {batchNo && (
                        <span className="flex items-center gap-1 border-l border-white/10 pl-4">
                            <span className="text-gray-500">{t('batch.current_active', '当前活动批次')}:</span>
                            <span className="font-mono font-black text-slate-900 dark:text-white tracking-widest">{batchNo}</span>
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/settings/bluetooth')}
                        className={`flex items-center gap-1 transition-all active:scale-95 px-1.5 py-0.5 rounded-md ${hasAnyDevice ? 'text-primary hover:bg-primary/10' : 'text-red-500 hover:bg-red-500/10'}`}
                    >
                        <span className="material-icons text-[12px] font-bold">
                            {hasAnyDevice ? 'bluetooth_connected' : 'bluetooth_disabled'}
                        </span>
                        <span className="font-bold">
                            {scaleDevice ? `${t('bluetooth.scale', '秤')}: ${scaleDevice.name}` : printerDevice ? `${t('bluetooth.printer', '打印机')}: ${printerDevice.name}` : t('bluetooth.no_device_connected', '蓝牙未连接')}
                        </span>
                        <span className={`material-icons text-[10px] ${hasAnyDevice ? 'text-primary/50' : 'text-red-500/50'}`}>chevron_right</span>
                    </button>
                    <span className="flex items-center gap-1">
                        <span className="material-icons text-[12px]">wifi</span>
                        <span className="font-bold">5G</span>
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="material-icons text-[12px]">battery_std</span>
                        <span className="font-bold">85%</span>
                    </span>
                    <div className="w-px h-3 bg-slate-200 dark:bg-white/20 mx-1"></div>
                    {/* Scenario Selector */}
                    <div className="flex items-center gap-1 flex-none">
                        <select
                            value={selectedTemplateId}
                            onChange={(e) => setFeatureTemplate('cargo_create', e.target.value)}
                            className="bg-transparent border-none p-0 focus:ring-0 text-[10px] font-bold text-primary dark:text-blue-400 outline-none uppercase tracking-wider cursor-pointer"
                        >
                            {templates.map(template => (
                                <option key={template.id} value={template.id} className="bg-white dark:bg-surface-dark text-slate-900 dark:text-white">
                                    {template.name} ({LABEL_SIZES[template.size].label})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
            <div className="px-5 py-4 flex justify-between items-center relative gap-4">
                <button
                    onClick={() => navigate('/sender')}
                    className="flex-none p-2 -ml-2 text-gray-400 hover:text-white rounded-full hover:bg-white/10 transition-colors z-10"
                >
                    <span className="material-icons">arrow_back</span>
                </button>
                <div className="flex-1 flex items-center justify-center gap-3 min-w-0">
                    <h1 className="text-lg font-bold text-white tracking-wide truncate">{t('cargo.create_cargo_only', '创建货物 (仅建档)')}</h1>
                    <div className="flex-none flex items-center gap-2 bg-white/5 border border-white/10 px-2 py-1 rounded-lg">
                        <div className="flex flex-col items-center leading-none px-1 border-r border-white/10">
                            <span className="text-[8px] font-black text-gray-500 uppercase tracking-tighter">{t('common.item_count', '件数')}</span>
                            <span className="text-xs font-bold text-primary">{totalCount}</span>
                        </div>
                        <div className="flex flex-col items-center leading-none px-1">
                            <span className="text-[8px] font-black text-gray-500 uppercase tracking-tighter">{t('common.total_weight', '总重')}</span>
                            <span className="text-xs font-bold text-primary font-mono">{totalWeight.toFixed(2)}<span className="text-[8px] ml-0.5 opacity-50">kg</span></span>
                        </div>
                    </div>
                </div>
                <div className="flex-none flex items-center gap-2 z-10">
                    <button
                        onClick={() => navigate('/sender/monitor')}
                        className="text-primary hover:text-blue-300 text-[10px] font-bold uppercase transition-all px-2 py-1.5 bg-primary/5 active:scale-95 border border-primary/20 rounded"
                    >
                        {t('cargo.monitor_button', '数据概览')}
                    </button>
                    <button
                        onClick={() => {
                            const event = new CustomEvent('generate-waybill');
                            window.dispatchEvent(event);
                        }}
                        className="text-primary hover:text-blue-300 text-[10px] font-bold uppercase transition-all px-2 py-1.5 bg-primary/5 active:scale-95 border border-primary/20 rounded"
                    >
                        {t('cargo.gen_waybill', '生成单号')}
                    </button>
                </div>
            </div>
        </header>
    );
};

export default CargoCreateHeader;
